import React, { useState } from 'react';
import {
  Box,
  List,
  ListItemButton,
  ListItemText,
  ListItemSecondaryAction,
  IconButton,
  Menu,
  MenuItem,
  Typography,
  Alert,
  Skeleton,
  Button,
  Tooltip,
} from '@mui/material';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import { useNavigate, useParams } from 'react-router-dom';

import { useDeleteUnitMutation, useMoveUnitMutation } from '../unitsApi';
import { useConfirm } from '../../../shared/components/ConfirmProvider';
import ClampedTextTooltip from '../../../shared/components/ClampedTextTooltip';
import {
  SortableList,
  type SortableListRenderProps,
} from '../../../shared/components/SortableList';
import type { ReorderPlacement } from '../../../shared/utils/reorder';

import { Unit } from '@/shared/types/unit';

interface UnitsNavProps {
  units: Unit[];
  isLoading?: boolean;
  error?: any;
  canEdit?: boolean;
  onCreateUnit?: () => void;
  backTo?: { label: string; path: string };
}

const UnitsNav: React.FC<UnitsNavProps> = ({
  units,
  isLoading,
  error,
  canEdit = false,
  onCreateUnit,
  backTo,
}) => {
  const navigate = useNavigate();
  const { unitId: activeUnitId, courseId } = useParams<{ unitId?: string; courseId?: string }>();
  const confirm = useConfirm();
  const [menuAnchorEl, setMenuAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedUnit, setSelectedUnit] = useState<Unit | null>(null);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const [deleteUnit] = useDeleteUnitMutation();
  const [moveUnit] = useMoveUnitMutation();

  const navigateToUnit = (unit: Unit) => {
    if (courseId) {
      navigate(`/curriculum/courses/${courseId}/units/${unit.id}`);
      return;
    }
    navigate(`/curriculum/units/${unit.id}`);
  };

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>, unit: Unit) => {
    event.preventDefault();
    event.stopPropagation();
    setErrorMsg(null);
    setMenuAnchorEl(event.currentTarget);
    setSelectedUnit(unit);
  };

  const handleMenuClose = () => {
    setMenuAnchorEl(null);
    setSelectedUnit(null);
  };

  const handleDelete = async () => {
    if (!selectedUnit) return;
    const target = selectedUnit;
    handleMenuClose();

    const confirmed = await confirm({
      title: 'Delete Unit',
      message: `Are you sure you want to delete "${target.title}"? This also deletes its lessons and resources. This action cannot be undone.`,
      confirmButtonText: 'Delete',
      cancelButtonText: 'Cancel',
    });
    if (!confirmed) return;

    try {
      await deleteUnit(target.id).unwrap();
      if (activeUnitId === target.id) {
        const parentPath = courseId ? `/curriculum/courses/${courseId}` : backTo?.path;
        if (parentPath) {
          void navigate(parentPath, { replace: true });
        }
      }
    } catch (err: unknown) {
      const message =
        typeof err === 'object' && err !== null && 'error' in err
          ? String((err as { error?: unknown }).error ?? 'Unknown error')
          : 'Please check your permissions and try again.';
      setErrorMsg(`Failed to delete "${target.title}": ${message}`);
    }
  };

  const sorted = [...units].sort((a, b) => (a.position ?? 0) - (b.position ?? 0));

  const handleMoveUnit = async (unitId: string, placement: ReorderPlacement) => {
    await moveUnit({ unitId, courseId: courseId!, placement }).unwrap();
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      {/* Back link */}
      {backTo && (
        <ListItemButton
          onClick={() => navigate(backTo.path)}
          dense
          sx={{
            px: 1.5,
            py: 0.75,
            borderBottom: '1px solid',
            borderColor: 'divider',
            gap: 0.25,
          }}
        >
          <ChevronLeftIcon fontSize="small" sx={{ color: 'text.secondary', mr: 0.25 }} />
          <Typography color="text.secondary" fontWeight={700} noWrap>
            {backTo.label}
          </Typography>
        </ListItemButton>
      )}

      {/* Header row */}
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          px: 1.5,
          py: 1,
        }}
      >
        <Typography color="text.secondary" sx={{ lineHeight: 1, fontWeight: 700 }}>
          Units
        </Typography>
        {canEdit && (
          <Tooltip title="New Unit">
            <IconButton size="small" onClick={onCreateUnit} aria-label="Create unit">
              <AddIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        )}
      </Box>

      {/* Error */}
      {errorMsg && (
        <Alert severity="error" sx={{ mx: 1, mb: 1, fontSize: '0.75rem' }}>
          {errorMsg}
        </Alert>
      )}

      {/* List */}
      <Box sx={{ flexGrow: 1, overflowY: 'auto' }}>
        {isLoading ? (
          <Box sx={{ px: 1.5 }}>
            {[1, 2, 3].map((i) => (
              <Skeleton key={i} height={36} sx={{ mb: 0.5, borderRadius: 1 }} />
            ))}
          </Box>
        ) : error ? (
          <Alert severity="error" sx={{ mx: 1 }}>
            Failed to load units.
          </Alert>
        ) : sorted.length === 0 ? (
          <Box sx={{ px: 2, py: 3, textAlign: 'center' }}>
            <Typography variant="body2" color="text.disabled">
              No units yet
            </Typography>
            {canEdit && onCreateUnit && (
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={onCreateUnit}
                fullWidth
                sx={{ mt: 1.5 }}
              >
                Create Unit
              </Button>
            )}
          </Box>
        ) : (
          <SortableList
            items={sorted}
            canEdit={canEdit}
            onMove={handleMoveUnit}
            getItemLabel={(unit) => `Reorder ${unit.title || 'Untitled unit'}`}
            reorderErrorMessage="Failed to reorder unit. Please try again."
            renderContainer={(children) => (
              <List dense disablePadding sx={{ px: 0.5 }}>
                {children}
              </List>
            )}
            renderItem={(unit, dragProps) => (
              <SortableUnitItem
                unit={unit}
                index={dragProps.index}
                isActive={unit.id === activeUnitId}
                canEdit={canEdit}
                onNavigate={navigateToUnit}
                onMenuOpen={handleMenuOpen}
                dragProps={dragProps}
              />
            )}
          />
        )}
      </Box>

      {/* Add button pinned at bottom (visible alternative when list is empty) */}
      {canEdit && !isLoading && sorted.length > 0 && (
        <Box sx={{ p: 1.5, pt: 0.5 }}>
          <Button
            size="small"
            startIcon={<AddIcon />}
            onClick={onCreateUnit}
            fullWidth
            variant="text"
            sx={{ justifyContent: 'flex-start', color: 'text.secondary' }}
          >
            New Unit
          </Button>
        </Box>
      )}

      <Menu
        anchorEl={menuAnchorEl}
        open={Boolean(menuAnchorEl)}
        onClose={handleMenuClose}
        slotProps={{ paper: { elevation: 2 } }}
      >
        <MenuItem onClick={handleDelete} sx={{ color: 'error.main', gap: 1 }}>
          <DeleteIcon fontSize="small" />
          Delete
        </MenuItem>
      </Menu>
    </Box>
  );
};

interface SortableUnitItemProps {
  unit: Unit;
  index: number;
  isActive: boolean;
  canEdit: boolean;
  onNavigate: (unit: Unit) => void;
  onMenuOpen: (event: React.MouseEvent<HTMLElement>, unit: Unit) => void;
  dragProps: SortableListRenderProps;
}

const SortableUnitItem: React.FC<SortableUnitItemProps> = ({
  unit,
  index,
  isActive,
  canEdit,
  onNavigate,
  onMenuOpen,
  dragProps,
}) => {
  const unitTitle = unit.title || 'Untitled unit';
  const { setNodeRef, style, dragHandle } = dragProps;

  return (
    <ListItemButton
      ref={setNodeRef}
      style={style}
      selected={isActive}
      onClick={() => onNavigate(unit)}
      sx={{
        borderRadius: 1,
        mb: 0.25,
        pr: canEdit ? 5 : 1.5,
      }}
    >
      {dragHandle && (
        <Box sx={{ display: 'flex', alignItems: 'center', mr: 0.5 }}>{dragHandle}</Box>
      )}
      <ListItemText
        sx={{ my: 0, minWidth: 0 }}
        disableTypography
        primary={
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
            <Typography
              variant="caption"
              sx={{ minWidth: 18, fontWeight: 700, color: 'text.disabled', flexShrink: 0 }}
            >
              {index + 1}
            </Typography>
            <ClampedTextTooltip
              text={unitTitle}
              lineClamp={3}
              sx={{ fontWeight: isActive ? 600 : 400, lineHeight: 1.35 }}
            />
          </Box>
        }
      />
      {canEdit && (
        <ListItemSecondaryAction>
          <IconButton
            size="small"
            edge="end"
            onClick={(e) => onMenuOpen(e, unit)}
            aria-label="Unit options"
          >
            <MoreVertIcon fontSize="small" />
          </IconButton>
        </ListItemSecondaryAction>
      )}
    </ListItemButton>
  );
};

export default UnitsNav;
